/**
 * Screen-space hit-testing for army + city clicks (v0M3.10).
 *
 * Only returns entities the player owns. AI + neutral clicks return
 * null — no interaction on foreign entities at v0M3.10 (tooltip is a
 * v0M3.10a candidate).
 *
 * Radii match the render shapes:
 *  - Army shield ≈ `baseRadius` from `drawArmies` in renderer.ts. That
 *    scales with zoom (6-11 px). Hit radius uses a fixed 14 px cushion
 *    so players don't need pixel-perfect aim.
 *  - Settlement icon ≈ `cityIconRadius(camera)` from city-icon.ts. Hit radius
 *    uses that + a 4 px cushion.
 */

import { screenToWorld, worldToScreen, pxPerCell } from './map/camera';
import { cityIconRadius } from './map/city-icon';
import type { Camera } from './map/camera';
import type { LoadedMap } from './map/map-loader';
import type { ArmyId, SettlementId, FactionId, GameState, ProvinceId, SpyId, WorldStructureId } from '../sim/state';
import type { BattleId } from '../sim/battle-state';
import { sortedIds, toProvinceId } from '../sim/state';

/** Threshold for army clicks — screen-space pixels around the shield's centre. */
const ARMY_HIT_PADDING_PX = 14;

/** Threshold for city clicks — screen-space cushion above the icon radius. */
const CITY_HIT_PADDING_PX = 4;

/**
 * Find the player-owned army closest to (sx, sy) within the hit radius.
 * Returns the ArmyId, or null if no owned army is within reach.
 */
export function findArmyAtScreen(
  state: GameState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
  sx: number,
  sy: number,
  playerFactionId: FactionId,
): ArmyId | null {
  let bestId: ArmyId | null = null;
  let bestDistSq = ARMY_HIT_PADDING_PX * ARMY_HIT_PADDING_PX;
  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId)!;
    if (army.ownerFactionId !== playerFactionId) continue;
    const armyScreen = worldToScreen(
      camera,
      canvasW,
      canvasH,
      army.position.x,
      army.position.y,
    );
    const dx = armyScreen.x - sx;
    const dy = armyScreen.y - sy;
    const distSq = dx * dx + dy * dy;
    if (distSq <= bestDistSq) {
      bestDistSq = distSq;
      bestId = armyId;
    }
  }
  return bestId;
}

/**
 * Find the player-owned city (any city — major or minor — in an owned
 * province) closest to the click point within the icon-radius cushion.
 * Returns the ProvinceId containing the clicked city, or null.
 */
export function findCityProvinceAtScreen(
  state: GameState,
  map: LoadedMap,
  camera: Camera,
  canvasW: number,
  canvasH: number,
  sx: number,
  sy: number,
  playerFactionId: FactionId,
): ProvinceId | null {
  const iconRadius = cityIconRadius(camera);
  const hitRadius = iconRadius + CITY_HIT_PADDING_PX;
  const hitRadiusSq = hitRadius * hitRadius;

  let bestProvinceId: ProvinceId | null = null;
  let bestDistSq = hitRadiusSq;

  for (const rawProvince of map.provinces) {
    // Skip provinces the player doesn't own.
    const provinceIdBrand = toProvinceId(rawProvince.id);
    const provinceState = state.provinces.get(provinceIdBrand);
    if (!provinceState) continue;
    if (provinceState.ownerFactionId !== playerFactionId) continue;

    // Check major + minor cities.
    const cityCells: Array<readonly [number, number]> = [
      ...(rawProvince.majorCity ? [rawProvince.majorCity.cell] : []),
      ...(rawProvince.minorCities?.map((m) => m.cell) ?? []),
    ];
    for (const [cx, cy] of cityCells) {
      const cityScreen = worldToScreen(camera, canvasW, canvasH, cx + 0.5, cy + 0.5);
      const dx = cityScreen.x - sx;
      const dy = cityScreen.y - sy;
      const distSq = dx * dx + dy * dy;
      if (distSq <= bestDistSq) {
        bestDistSq = distSq;
        bestProvinceId = provinceIdBrand;
      }
    }
  }
  return bestProvinceId;
}

/**
 * Convert a screen point to a rounded world cell for issuing `move_army`
 * commands. Clamps to map bounds. `null` result means the click landed
 * outside the map area (rare but possible when the camera is scrolled
 * beyond an edge).
 */
export function screenToCell(
  camera: Camera,
  canvasW: number,
  canvasH: number,
  sx: number,
  sy: number,
  mapWidth: number,
  mapHeight: number,
): { x: number; y: number } | null {
  const world = screenToWorld(camera, canvasW, canvasH, sx, sy);
  const x = Math.floor(world.x);
  const y = Math.floor(world.y);
  if (x < 0 || y < 0 || x >= mapWidth || y >= mapHeight) return null;
  return { x, y };
}

/**
 * Find the active battle whose crossed-swords icon is closest to (sx, sy)
 * within the icon hit radius (v0M3.5). Only returns battles where the
 * player owns at least one participating army — foreign battles show
 * the icon (players can see the fighting) but can't take command of it.
 */
export function findBattleAtScreen(
  state: GameState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
  sx: number,
  sy: number,
  playerFactionId: FactionId,
): BattleId | null {
  const BATTLE_ICON_HIT_RADIUS_PX = 18;
  const bestSq = BATTLE_ICON_HIT_RADIUS_PX * BATTLE_ICON_HIT_RADIUS_PX;
  let best: BattleId | null = null;
  let bestDistSq = bestSq;
  for (const battleId of sortedIds(state.battles)) {
    const battle = state.battles.get(battleId)!;
    if (battle.result !== null) continue;
    const [aId, bId] = battle.armyIds;
    const armyA = state.armies.get(aId);
    const armyB = state.armies.get(bId);
    if (!armyA || !armyB) continue;
    const playerOwnsParticipant =
      armyA.ownerFactionId === playerFactionId || armyB.ownerFactionId === playerFactionId;
    if (!playerOwnsParticipant) continue;
    // MFix6.1: use the shared world-pos helper so hit-test matches the
    // renderer's icon position (including city-overlap avoidance).
    const iconPos = computeBattleIconWorldPos(state, armyA, armyB);
    const s = worldToScreen(camera, canvasW, canvasH, iconPos.x, iconPos.y);
    const dx = s.x - sx;
    const dy = s.y - sy;
    const distSq = dx * dx + dy * dy;
    if (distSq <= bestDistSq) {
      bestDistSq = distSq;
      best = battleId;
    }
  }
  return best;
}

/**
 * MFix6.1 (2026-07-23) — Compute the world-space position of a battle
 * icon. Base position is the midpoint of the two participating armies,
 * shifted vertically upward when that midpoint lands within 1 cell of
 * any city. Keeps the city sprite clickable when a battle spawns
 * right on top of it.
 *
 * Shared by both `renderer.ts::drawBattleIcons` (draw position) and
 * `hit-test.ts::findBattleAtScreen` (click target) so rendering and
 * hit-testing agree byte-for-byte.
 */
export function computeBattleIconWorldPos(
  state: GameState,
  armyA: { position: { x: number; y: number } },
  armyB: { position: { x: number; y: number } },
): { x: number; y: number } {
  const midX = (armyA.position.x + armyB.position.x) / 2;
  const midY = (armyA.position.y + armyB.position.y) / 2;
  // Overlap check: if any city sits within 1 Chebyshev cell of the
  // midpoint, shift the icon 1.2 cells upward so the city sprite stays
  // clickable. Cheap O(cities) per battle — v1 has ~100 cities max.
  for (const city of state.settlements.values()) {
    const dx = Math.abs(midX - (city.cell.x + 0.5));
    const dy = Math.abs(midY - (city.cell.y + 0.5));
    if (Math.max(dx, dy) <= 1.0) {
      return { x: midX, y: midY - 1.2 };
    }
  }
  return { x: midX, y: midY };
}

/**
 * MFix5.4 (2026-07-22) — Find the besieged city whose siege icon is
 * closest to (sx, sy) within the icon hit radius. Only returns sieges
 * where the player's faction owns the sieging army — foreign sieges
 * show the icon (siege is visible to everyone) but the Assault menu
 * only opens for the player's own sieges.
 */
export function findSiegeAtScreen(
  state: GameState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
  sx: number,
  sy: number,
  playerFactionId: FactionId,
): SettlementId | null {
  const SIEGE_ICON_HIT_RADIUS_PX = 18;
  const bestSq = SIEGE_ICON_HIT_RADIUS_PX * SIEGE_ICON_HIT_RADIUS_PX;
  let best: SettlementId | null = null;
  let bestDistSq = bestSq;
  for (const settlementId of sortedIds(state.settlements)) {
    const city = state.settlements.get(settlementId)!;
    if (city.siegeState === null) continue;
    const sieger = state.armies.get(city.siegeState.siegingArmyId);
    if (!sieger) continue;
    if (sieger.ownerFactionId !== playerFactionId) continue;
    // Icon position matches drawSiegeIcons: midpoint of city + sieger.
    const midX = (city.cell.x + sieger.position.x) / 2 + 0.5;
    const midY = (city.cell.y + sieger.position.y) / 2 + 0.5;
    const s = worldToScreen(camera, canvasW, canvasH, midX, midY);
    const dx = s.x - sx;
    const dy = s.y - sy;
    const distSq = dx * dx + dy * dy;
    if (distSq <= bestDistSq) {
      bestDistSq = distSq;
      best = settlementId;
    }
  }
  return best;
}

/**
 * v0M9 (2026-07-27) — Find the closest owned MINOR settlement to
 * (sx, sy) within the city-icon hit radius. Returns the SettlementId
 * or null. Ownership follows province (minors are owned by their
 * province owner; no independent ownership). Used by main.ts LMB
 * handler to route clicks on minors to the minor-settlement panel
 * (repair / pillage status) instead of the province-scoped
 * settlement panel.
 *
 * Call BEFORE `findCityProvinceAtScreen` — a click on a minor should
 * open the minor panel, not the province panel.
 */
export function findMinorSettlementAtScreen(
  state: GameState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
  sx: number,
  sy: number,
  playerFactionId: FactionId,
): SettlementId | null {
  const iconRadius = cityIconRadius(camera);
  const hitRadius = iconRadius + CITY_HIT_PADDING_PX;
  const hitRadiusSq = hitRadius * hitRadius;

  let bestId: SettlementId | null = null;
  let bestDistSq = hitRadiusSq;

  for (const settlementId of sortedIds(state.settlements)) {
    const settlement = state.settlements.get(settlementId);
    if (!settlement) continue;
    if (!settlement.isMinor) continue;
    const province = state.provinces.get(settlement.provinceId);
    if (!province) continue;
    if (province.ownerFactionId !== playerFactionId) continue;
    const cityScreen = worldToScreen(
      camera,
      canvasW,
      canvasH,
      settlement.cell.x + 0.5,
      settlement.cell.y + 0.5,
    );
    const dx = cityScreen.x - sx;
    const dy = cityScreen.y - sy;
    const distSq = dx * dx + dy * dy;
    if (distSq <= bestDistSq) {
      bestDistSq = distSq;
      bestId = settlementId;
    }
  }
  return bestId;
}

/**
 * v0M5.4 (2026-07-25) — Find ANY settlement (own or foreign) closest
 * to (sx, sy) within the city icon's hit radius. Used by the spy
 * enter-settlement flow — spies infiltrate enemy/neutral settlements,
 * so ownership filtering doesn't apply. Returns the SettlementId, or
 * null if no city is within reach.
 */
export function findAnySettlementAtScreen(
  state: GameState,
  map: LoadedMap,
  camera: Camera,
  canvasW: number,
  canvasH: number,
  sx: number,
  sy: number,
): SettlementId | null {
  const iconRadius = cityIconRadius(camera);
  const hitRadius = iconRadius + CITY_HIT_PADDING_PX;
  const hitRadiusSq = hitRadius * hitRadius;

  let bestId: SettlementId | null = null;
  let bestDistSq = hitRadiusSq;

  // Iterate the sim's actual settlements (materialized via scenario boot
  // + MFix5+fix3) rather than raw map data — the sim state carries the
  // authoritative settlement ids.
  for (const settlementId of sortedIds(state.settlements)) {
    const settlement = state.settlements.get(settlementId);
    if (!settlement) continue;
    const cityScreen = worldToScreen(
      camera,
      canvasW,
      canvasH,
      settlement.cell.x + 0.5,
      settlement.cell.y + 0.5,
    );
    const dx = cityScreen.x - sx;
    const dy = cityScreen.y - sy;
    const distSq = dx * dx + dy * dy;
    if (distSq <= bestDistSq) {
      bestDistSq = distSq;
      bestId = settlementId;
    }
  }
  // Silence unused-param warning; map arg reserved for future filters.
  void map;
  return bestId;
}

/**
 * v0M7.4d+garrison.2 (2026-07-27) — Find the player-owned Fort closest
 * to (sx, sy) within the city-icon hit radius. Returns the
 * WorldStructureId or null. Only owner Forts are hit-testable —
 * clicking an enemy Fort routes elsewhere (siege icon if besieged,
 * else no-op). Radius uses the same city-icon cushion since forts
 * render at roughly city-icon scale.
 *
 * Call AFTER army/spy/battle/siege/minor/city hits — those bigger
 * targets take priority when overlapping a Fort icon.
 */
export function findFortAtScreen(
  state: GameState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
  sx: number,
  sy: number,
  playerFactionId: FactionId,
): WorldStructureId | null {
  if (!state.worldStructures) return null;
  const iconRadius = cityIconRadius(camera);
  const hitRadius = iconRadius + CITY_HIT_PADDING_PX;
  const hitRadiusSq = hitRadius * hitRadius;

  let bestId: WorldStructureId | null = null;
  let bestDistSq = hitRadiusSq;

  for (const wsId of sortedIds(state.worldStructures)) {
    const ws = state.worldStructures.get(wsId);
    if (!ws) continue;
    if (ws.kind !== 'fort') continue;
    if (ws.ownerFactionId !== playerFactionId) continue;
    const s = worldToScreen(
      camera,
      canvasW,
      canvasH,
      ws.cell.x + 0.5,
      ws.cell.y + 0.5,
    );
    const dx = s.x - sx;
    const dy = s.y - sy;
    const distSq = dx * dx + dy * dy;
    if (distSq <= bestDistSq) {
      bestDistSq = distSq;
      bestId = wsId;
    }
  }
  return bestId;
}

/**
 * v0M5.3 (2026-07-25) — Find the player-owned Spy closest to (sx, sy)
 * within the hit radius. Only own spies are hit-testable; enemy spies
 * are visually filtered by the fog (state-4 required) and never
 * selectable by the player. Radius uses the same ARMY_HIT_PADDING_PX
 * cushion — spies are tiny icons but the click target stays generous.
 */
export function findSpyAtScreen(
  state: GameState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
  sx: number,
  sy: number,
  playerFactionId: FactionId,
): SpyId | null {
  if (!state.spies) return null;
  let bestId: SpyId | null = null;
  let bestDistSq = ARMY_HIT_PADDING_PX * ARMY_HIT_PADDING_PX;
  for (const spyId of sortedIds(state.spies)) {
    const spy = state.spies.get(spyId)!;
    if (spy.ownerFactionId !== playerFactionId) continue;
    const spyScreen = worldToScreen(camera, canvasW, canvasH, spy.position.x, spy.position.y);
    const dx = spyScreen.x - sx;
    const dy = spyScreen.y - sy;
    const distSq = dx * dx + dy * dy;
    if (distSq <= bestDistSq) {
      bestDistSq = distSq;
      bestId = spyId;
    }
  }
  return bestId;
}

/** Convenience export for hover/preview code that needs the zoom step. */
export { pxPerCell };
